import type { ExportableSeries, HighchartsWrapperProps, OperatorCategoricalData, RecordValue } from "../types";
import { isRecord } from "./isRecord";

const normalizeCategoryLabel = (value: unknown): string | null => {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  return null;
};

const serializeCategoryValue = (value: unknown): number | null => {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

const getSeriesXAxis = (seriesOptions: RecordValue, xAxes: RecordValue[]) => {
  const xAxisReference = seriesOptions.xAxis;

  if (typeof xAxisReference === "number" && Number.isInteger(xAxisReference)) {
    return xAxes[xAxisReference];
  }

  if (typeof xAxisReference === "string") {
    return xAxes.find((xAxis) => xAxis.id === xAxisReference);
  }

  return xAxes[0];
};

const getSeriesType = (seriesOptions: RecordValue, chartType: unknown): string => {
  if (typeof seriesOptions.type === "string") {
    return seriesOptions.type;
  }

  return typeof chartType === "string" ? chartType : "line";
};

const getSeriesName = (seriesOptions: RecordValue, index: number): string => {
  return typeof seriesOptions.name === "string" && seriesOptions.name.length > 0
    ? seriesOptions.name
    : `Series ${index + 1}`;
};

const getCategoryAt = (categories: unknown[], index: number): string | null => {
  return Number.isInteger(index) && index >= 0 ? normalizeCategoryLabel(categories[index]) : null;
};

const getCategoryForPointName = (
  name: unknown,
  categories: unknown[],
  allocateAutoCategory: () => string | null
): string | null => {
  const normalizedName = normalizeCategoryLabel(name);
  if (normalizedName === null) {
    return allocateAutoCategory();
  }

  const matchingIndex = categories.findIndex((category) => normalizeCategoryLabel(category) === normalizedName);
  return matchingIndex < 0 ? allocateAutoCategory() : getCategoryAt(categories, matchingIndex);
};

/**
 * Converts Highcharts category data to the small normalized point contract
 * used by Operator. Invalid values become null y values while their category
 * position is retained whenever the category itself is valid.
 */
const serializeCategoryPoint = (
  point: unknown,
  categories: unknown[],
  allocateAutoCategory: () => string | null
): [string, number | null] | null => {
  if (Array.isArray(point)) {
    const x = point[0];
    let category: string | null;
    if (typeof x === "number") {
      category = Number.isFinite(x) && Number.isInteger(x) ? getCategoryAt(categories, x) : null;
    } else if (typeof x === "string") {
      category = getCategoryForPointName(x, categories, allocateAutoCategory);
    } else {
      category = allocateAutoCategory();
    }

    const y = typeof x === "number" || typeof x === "string" ? point[1] : point[0];
    return category === null ? null : [category, serializeCategoryValue(y)];
  }

  if (isRecord(point)) {
    let category: string | null;
    if (typeof point.x === "number") {
      category = Number.isFinite(point.x) && Number.isInteger(point.x) ? getCategoryAt(categories, point.x) : null;
    } else if (typeof point.x === "string") {
      category = null;
    } else {
      category = getCategoryForPointName(point.name, categories, allocateAutoCategory);
    }

    return category === null ? null : [category, serializeCategoryValue(point.y)];
  }

  const category = allocateAutoCategory();
  return category === null ? null : [category, serializeCategoryValue(point)];
};

/**
 * Extracts an explicitly opted-in category chart. Category and datetime
 * series in one exportable config are rejected here; the legacy datetime
 * normalizer owns that mixed-axis behavior for existing callers.
 */
export const getOperatorCategoricalData = (
  chartConfig: HighchartsWrapperProps["chartConfig"]
): OperatorCategoricalData | undefined => {
  const xAxes = (Array.isArray(chartConfig.xAxis) ? chartConfig.xAxis : [chartConfig.xAxis]).filter(isRecord);
  const categoryAxes = xAxes.filter((xAxis) => xAxis.type !== "datetime" && Array.isArray(xAxis.categories));

  if (categoryAxes.length === 0) {
    return undefined;
  }

  const exportableSeries: ExportableSeries[] = (chartConfig.series || []).flatMap((seriesOptions, index) => {
    if (!isRecord(seriesOptions) || !Array.isArray(seriesOptions.data)) {
      return [];
    }

    if (seriesOptions.includeInDataExport === false) {
      return [];
    }

    const xAxis = getSeriesXAxis(seriesOptions, xAxes);
    if (!xAxis) {
      return [];
    }

    return [{ seriesOptions, xAxis, index }];
  });
  const hasDatetimeSeries = exportableSeries.some(({ xAxis }) => xAxis.type === "datetime");
  const categoricalSeries = exportableSeries.filter(({ xAxis }) => categoryAxes.includes(xAxis));

  if (hasDatetimeSeries || categoricalSeries.length === 0) {
    return undefined;
  }

  const series = categoricalSeries.map(({ seriesOptions, index, xAxis }) => {
    const categories = xAxis.categories as unknown[];
    let autoIncrement =
      typeof seriesOptions.pointStart === "number" && Number.isFinite(seriesOptions.pointStart)
        ? seriesOptions.pointStart
        : 0;
    const pointInterval =
      typeof seriesOptions.pointInterval === "number" && Number.isFinite(seriesOptions.pointInterval)
        ? seriesOptions.pointInterval
        : 1;
    const allocateAutoCategory = (): string | null => {
      const category = getCategoryAt(categories, autoIncrement);
      autoIncrement += pointInterval;
      return category;
    };
    const serializedSeries = {
      name: getSeriesName(seriesOptions, index),
      type: getSeriesType(seriesOptions, chartConfig.chart?.type),
      keys: ["category", "y"] as ["category", "y"],
      data: (seriesOptions.data as unknown[]).flatMap((point) => {
        const serializedPoint = serializeCategoryPoint(point, categories, allocateAutoCategory);
        return serializedPoint === null ? [] : [serializedPoint];
      }),
    };

    return serializedSeries;
  });

  return {
    type: "categorical",
    xAxis: { type: "category" },
    series,
  };
};
